/* eslint-disable @typescript-eslint/no-explicit-any */
// pls fix me
import axios from 'axios';

export enum OperatingSystem {
  Windows = 'Windows',
  Mac = 'Mac',
  Others = 'Others',
}

type DownloadContent = BlobPart | null | undefined;

const isValidDownloadContent = (content: DownloadContent): content is BlobPart =>
  content !== null && content !== undefined;

export const openLink = (url: string) => {
  window.open(url, '_blank', 'noopener');
};

const downloadUrl = async (url: string) => {
  const response = await axios.get<ArrayBuffer>(url, {
    responseType: 'arraybuffer',
  });

  return response.data;
};

const simulateLink = (content: DownloadContent, fileName: string, mimeType?: string) => {
  if (!isValidDownloadContent(content)) {
    throw new Error('Invalid file content, download skipped.');
  }

  const blob = content instanceof Blob ? content : new Blob([content], { type: mimeType });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');

  try {
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
  } finally {
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }
};

export const download = async (
  content: DownloadContent | string,
  fileName: string,
  useWindowOpen = false,
) => {
  try {
    if (!content) {
      throw new Error('Invalid download content, download skipped.');
    }

    if (typeof content === 'string') {
      if (useWindowOpen) {
        window.open(content);
        return;
      }

      const data = await downloadUrl(content);

      simulateLink(data, fileName);
      return;
    }

    simulateLink(content, fileName);
  } catch (error) {
    console.error('Error downloading file', error);
  }
};

export const getOperatingSystem = (): OperatingSystem => {
  const platform = navigator.userAgent;
  if (platform.indexOf('Win') !== -1) {
    return OperatingSystem.Windows;
  }
  if (platform.indexOf('Mac') !== -1) {
    return OperatingSystem.Mac;
  }
  return OperatingSystem.Others;
};

/**
 * Converts the given File into a Data URL string
 */
function arrayBufferToBase64(buffer: ArrayBuffer): string {
  let binary = '';
  const bytes = new Uint8Array(buffer);
  const len = bytes.byteLength;

  for (let i = 0; i < len; i++) {
    binary += String.fromCharCode(bytes[i]);
  }

  return window.btoa(binary);
}

export const convertFileToDataURL = (file: File) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.addEventListener(
      'load',
      () => {
        const { result } = reader;

        if (result instanceof ArrayBuffer) {
          resolve(`data:image/png;base64,${arrayBufferToBase64(result)}`);
          return;
        }

        if (typeof result === 'string') {
          resolve(result);
          return;
        }

        reject(new Error('Failed to convert file to data URL.'));
      },
      false,
    );
    reader.addEventListener('error', (event) => reject(event), false);
    reader.readAsDataURL(file);
  });

/**
 * Converts the given Data URL string to a File
 */
export const convertDataUrlToFile = async (url: string, fileName: string) => {
  const blob = await (await fetch(url)).blob();
  return new File([blob], fileName, { type: blob.type });
};

/**
 * Parses the given text into an xml document
 *
 * @throws When parsing fails
 */
export const parseXMLDocument = (text: string, mimeType: DOMParserSupportedType) => {
  const document = new DOMParser().parseFromString(text, mimeType);

  // Chrome/Edge error
  let errorNode = document.querySelector('parsererror div');

  // Firefox error
  if (!errorNode) {
    errorNode = document.querySelector('parsererror');
  }

  if (errorNode) {
    throw new Error(errorNode.textContent ?? 'Failed to parse XML document.');
  }

  return document;
};

/**
 * Opens file select dialog.
 *
 * @returns Promise that resolves to a FileList or null if the user cancelled the operation
 */
export const openFile = (accept: string) =>
  new Promise<FileList | null>((resolve) => {
    const input = document.createElement('input');
    input.style.display = 'none';
    input.type = 'file';
    input.accept = accept;
    input.addEventListener('change', () => {
      resolve(input.files);
    });
    input.addEventListener('cancel', () => {
      resolve(null);
    });
    input.click();
  });

export const downloadFile = (data: Blob | null | undefined, mimeType: string, fileName: string) => {
  if (!data || data.size === 0) {
    throw new Error('Invalid file data, download skipped.');
  }

  const blob = new Blob([data], { type: mimeType });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');

  try {
    link.href = url;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
  } finally {
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }
};
